import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  Shield, Eye, Brain, Fingerprint, Target, Globe,
  AlertTriangle, TrendingUp, Lock, Activity, Crosshair,
  DollarSign, Radio, Cpu, Search, CheckCircle, XCircle
} from 'lucide-react';

import { authFetch, API_URL } from '../utils/authFetch';

const SecurityIntelligence = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [zeroDays, setZeroDays] = useState(null);
  const [crossProtocol, setCrossProtocol] = useState(null);
  const [financialFraud, setFinancialFraud] = useState(null);
  const [telecomThreats, setTelecomThreats] = useState(null);
  const [biometrics, setBiometrics] = useState(null);
  const [hunting, setHunting] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, zdRes, cpRes, ffRes, ttRes, bioRes, huntRes] = await Promise.all([
        authFetch(`${API_URL}/api/securesphere/intelligence/dashboard`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/intelligence/zero-day-detection`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/intelligence/cross-protocol-correlation`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/intelligence/financial-fraud-correlation`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/intelligence/telecom-specific-threats`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/intelligence/behavioral-biometrics`).then(r => r.json()),
        authFetch(`${API_URL}/api/securesphere/intelligence/predictive-hunting`).then(r => r.json())
      ]);
      setDashboard(dashRes);
      setZeroDays(zdRes);
      setCrossProtocol(cpRes);
      setFinancialFraud(ffRes);
      setTelecomThreats(ttRes);
      setBiometrics(bioRes);
      setHunting(huntRes);
    } catch (err) {
      // Silently handle fetch errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 45000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const threatLevelColors = useMemo(() => ({
    elevated: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    severe: 'bg-red-500/20 text-red-400 border-red-500/30'
  }), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="intel-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-intelligence">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Security Intelligence</h2>
          <p className="text-neutral-400">Gap-filling features vs. Aeris WatchTower & PaloAlto</p>
        </div>
        {dashboard && (
          <Badge className={threatLevelColors[dashboard.threat_landscape.global_threat_level] || threatLevelColors.elevated}>
            <AlertTriangle className="w-3 h-3 mr-1" />
            Threat Level: {dashboard.threat_landscape.global_threat_level.toUpperCase()}
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="zero-day">Zero-Day</TabsTrigger>
          <TabsTrigger value="cross-protocol">Cross-Protocol</TabsTrigger>
          <TabsTrigger value="financial">Financial Fraud</TabsTrigger>
          <TabsTrigger value="telecom">Telecom Threats</TabsTrigger>
          <TabsTrigger value="biometrics">Biometrics</TabsTrigger>
          <TabsTrigger value="hunting">Threat Hunting</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-6">
          {dashboard && (
            <>
              {/* Threat Landscape */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-red-500/20 to-orange-500/10 border-red-500/30">
                  <CardContent className="p-4">
                    <Target className="w-6 h-6 text-red-400 mb-2" />
                    <p className="text-xs text-neutral-400">Active Campaigns</p>
                    <p className="text-2xl font-bold text-white">{dashboard.threat_landscape.active_campaigns}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Eye className="w-6 h-6 text-orange-400 mb-2" />
                    <p className="text-xs text-neutral-400">Zero-Days Tracked</p>
                    <p className="text-2xl font-bold text-white">{dashboard.threat_landscape.zero_days_tracked}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Shield className="w-6 h-6 text-green-400 mb-2" />
                    <p className="text-xs text-neutral-400">Threats Blocked (24h)</p>
                    <p className="text-2xl font-bold text-white">{dashboard.your_network.threats_blocked_24h.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Activity className="w-6 h-6 text-purple-400 mb-2" />
                    <p className="text-xs text-neutral-400">Risk Score</p>
                    <p className="text-2xl font-bold text-white">{dashboard.your_network.risk_score}/100</p>
                  </CardContent>
                </Card>
              </div>

              {/* Unique Capabilities */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Unique Capabilities (vs Competitors)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(dashboard.unique_capabilities).map(([key, cap]) => (
                      <div key={key} className="bg-[#1a1a1a] rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            {cap.status || 'active'}
                          </Badge>
                        </div>
                        <p className="text-sm text-white font-semibold mb-1 capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-neutral-400">{cap.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Competitor Comparison */}
              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(dashboard.comparison_vs_competitors).map(([competitor, data]) => (
                  <Card key={competitor} className="bg-[#242424] border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white capitalize flex items-center gap-2">
                        <Crosshair className="w-5 h-5 text-orange-400" />
                        vs {competitor.replace(/_/g, ' ')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {data.we_offer_extra.map((feature) => (
                          <div key={feature} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-neutral-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Zero-Day Detection */}
        <TabsContent value="zero-day" className="space-y-6">
          {zeroDays && (
            <>
              <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">AI Zero-Day Detection</h3>
                      <p className="text-neutral-300">{zeroDays.detection_method}</p>
                      <p className="text-sm text-green-400 mt-1">{zeroDays.advantage}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-white">{zeroDays.detection_stats.zero_days_caught_30d}</p>
                      <p className="text-xs text-neutral-400">caught in 30 days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4">
                {zeroDays.active_detections.map((detection) => (
                  <Card key={detection.name || detection.cve_id} className="bg-[#242424] border-white/10">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-orange-400 border-orange-500/30 font-mono text-xs">
                              {detection.id}
                            </Badge>
                            <h4 className="text-white font-semibold">{detection.name}</h4>
                          </div>
                          <p className="text-sm text-neutral-400 mt-1">{detection.ai_analysis}</p>
                        </div>
                        <Badge className={
                          detection.status === 'contained' ? 'bg-green-500/20 text-green-400' :
                          detection.status === 'investigating' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-blue-500/20 text-blue-400'
                        }>
                          {detection.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-neutral-400">Devices: <span className="text-white">{detection.affected_devices}</span></span>
                        <span className="text-neutral-400">Confidence: <span className="text-white">{(detection.confidence * 100).toFixed(0)}%</span></span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {detection.behavior_indicators.map((ind) => (
                          <Badge key={ind} variant="outline" className="text-neutral-300 border-white/20 text-xs">
                            {ind}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Cross-Protocol */}
        <TabsContent value="cross-protocol" className="space-y-6">
          {crossProtocol && (
            <>
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Globe className="w-5 h-5 text-orange-400" />
                    Protocols Monitored
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {crossProtocol.protocols_monitored.map((proto) => (
                      <div key={proto.name} className="bg-[#1a1a1a] rounded-lg p-3">
                        <p className="text-white font-semibold text-sm">{proto.name}</p>
                        <p className="text-xs text-neutral-400">Coverage: {proto.coverage}</p>
                        <p className="text-xs text-orange-400">{proto.events_24h.toLocaleString()} events/24h</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {crossProtocol.correlated_threats.map((threat) => (
                <Card key={threat.name || threat.id} className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-red-400" />
                      {threat.threat_type}
                      <Badge className="bg-red-500/20 text-red-400 ml-2">
                        Risk: {threat.risk_score}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-neutral-400 mb-4">{threat.description}</p>
                    <div className="space-y-3">
                      {threat.timeline.map((event) => (
                        <div key={event.time} className="flex items-start gap-3">
                          <Badge variant="outline" className="text-orange-400 border-orange-500/30 min-w-[60px] justify-center text-xs">
                            {event.time}
                          </Badge>
                          <span className="text-sm text-neutral-300">{event.event}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Card className="bg-[#242424] border-white/10">
                <CardContent className="p-5">
                  <p className="text-green-400 font-semibold">{crossProtocol.insights.correlation_effectiveness}</p>
                  <p className="text-sm text-neutral-400 mt-1">
                    {crossProtocol.insights.attacks_spanning_multiple_protocols} multi-protocol attacks detected |
                    {crossProtocol.insights.unique_attack_patterns_discovered} unique patterns discovered
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Financial Fraud */}
        <TabsContent value="financial" className="space-y-6">
          {financialFraud && (
            <>
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
                  <CardContent className="p-4">
                    <DollarSign className="w-6 h-6 text-green-400 mb-2" />
                    <p className="text-xs text-neutral-400">Value Protected (30d)</p>
                    <p className="text-xl font-bold text-white">{financialFraud.stats_30d.value_protected}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Shield className="w-6 h-6 text-orange-400 mb-2" />
                    <p className="text-xs text-neutral-400">Fraud Detected</p>
                    <p className="text-xl font-bold text-white">{financialFraud.stats_30d.fraud_attempts_detected}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Activity className="w-6 h-6 text-blue-400 mb-2" />
                    <p className="text-xs text-neutral-400">Avg Detection</p>
                    <p className="text-xl font-bold text-white">{financialFraud.stats_30d.avg_detection_time_seconds}s</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <XCircle className="w-6 h-6 text-red-400 mb-2" />
                    <p className="text-xs text-neutral-400">False Positive</p>
                    <p className="text-xl font-bold text-white">{financialFraud.stats_30d.false_positive_rate}%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Telecom Fraud Types */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Telecom-Linked Fraud Types</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(financialFraud.telecom_fraud_types).map(([type, data]) => (
                      <div key={type} className="bg-[#1a1a1a] rounded-lg p-4 flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold capitalize">{type.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-neutral-400">Detected: {data.detected} | Blocked: {data.blocked}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold">{data.blocked > 0 ? ((data.blocked / data.detected) * 100).toFixed(0) : 0}%</p>
                          <p className="text-xs text-neutral-500">block rate</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Active Correlations */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Active Fraud Correlations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {financialFraud.active_correlations.slice(0, 5).map((corr) => (
                      <div key={corr.correlation_id || corr.id} className="bg-[#1a1a1a] rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-orange-400 border-orange-500/30 font-mono text-xs">
                                {corr.id}
                              </Badge>
                              <span className="text-white font-semibold">{corr.type}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-red-500/20 text-red-400">Risk: {corr.risk_score}</Badge>
                            <p className="text-xs text-neutral-500 mt-1">At risk: {corr.value_at_risk}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3 mt-2">
                          <div>
                            <p className="text-xs text-neutral-500 mb-1">Network Indicators:</p>
                            <div className="flex flex-wrap gap-1">
                              {corr.network_indicators.map((ind) => (
                                <Badge key={ind} variant="outline" className="text-neutral-300 border-white/20 text-xs">{ind}</Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500 mb-1">Financial Indicators:</p>
                            <div className="flex flex-wrap gap-1">
                              {corr.financial_indicators.map((ind) => (
                                <Badge key={ind} variant="outline" className="text-red-300 border-red-500/20 text-xs">{ind}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          <Badge className={corr.status === 'blocked' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                            {corr.status}
                          </Badge>
                          <span className="text-neutral-500">Detection time: {corr.time_to_detect}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Telecom Threats */}
        <TabsContent value="telecom" className="space-y-6">
          {telecomThreats && (
            <>
              {/* SS7 */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Radio className="w-5 h-5 text-red-400" />
                    SS7 Signaling Threats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      {telecomThreats.ss7_threats.attack_types.map((atk) => (
                        <div key={atk.type} className="flex items-center justify-between bg-[#1a1a1a] rounded p-3">
                          <span className="text-sm text-neutral-300">{atk.type}</span>
                          <Badge className="bg-red-500/20 text-red-400">{atk.count}</Badge>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#1a1a1a] rounded-lg p-4">
                      <p className="text-white font-semibold mb-3">Protection Status</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-400">Attacks (24h)</span>
                          <span className="text-red-400">{telecomThreats.ss7_threats.attacks_detected_24h}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-400">Protected Subscribers</span>
                          <span className="text-green-400">{telecomThreats.ss7_threats.protected_subscribers.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-400">Diameter Blocked</span>
                          <span className="text-white">{telecomThreats.diameter_threats.attacks_blocked_24h}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-400">GTP Anomalies</span>
                          <span className="text-white">{telecomThreats.gtp_threats.anomalies_detected}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SIM Swap & IMSI */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">SIM Swap Intelligence</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">High Risk Numbers</span>
                        <span className="text-orange-400 font-bold">{telecomThreats.sim_swap_intelligence.high_risk_numbers}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Swap Attempts Blocked</span>
                        <span className="text-green-400 font-bold">{telecomThreats.sim_swap_intelligence.swap_attempts_blocked}</span>
                      </div>
                      <div className="mt-3">
                        <p className="text-xs text-neutral-500 mb-2">Carrier Integration:</p>
                        <div className="flex gap-2 flex-wrap">
                          {telecomThreats.sim_swap_intelligence.carrier_integration.map((c) => (
                            <Badge key={c} variant="outline" className="text-green-400 border-green-500/30">{c}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#242424] border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">IMSI Catcher Detection</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Fake Towers Found</span>
                        <span className="text-red-400 font-bold">{telecomThreats.imsi_catcher_detection.fake_towers_identified}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Alerts Sent</span>
                        <span className="text-orange-400 font-bold">{telecomThreats.imsi_catcher_detection.alerts_sent}</span>
                      </div>
                      <div className="mt-3">
                        <p className="text-xs text-neutral-500 mb-2">Affected Areas:</p>
                        <div className="flex gap-2 flex-wrap">
                          {telecomThreats.imsi_catcher_detection.affected_areas.map((a) => (
                            <Badge key={a} variant="outline" className="text-red-400 border-red-500/30">{a}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Roaming Fraud */}
              <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
                <CardContent className="p-5 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold">Roaming Fraud Protection</h3>
                    <p className="text-sm text-neutral-400">Suspicious roaming detected: {telecomThreats.roaming_fraud.suspicious_roaming_detected}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-400">{telecomThreats.roaming_fraud.revenue_protected}</p>
                    <p className="text-xs text-neutral-500">revenue protected</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Biometrics */}
        <TabsContent value="biometrics" className="space-y-6">
          {biometrics && (
            <>
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Fingerprint className="w-5 h-5 text-purple-400" />
                    Behavioral Biometric Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {biometrics.biometric_types.map((bio) => (
                      <div key={bio.type || bio.name} className="bg-[#1a1a1a] rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-white font-semibold">{bio.type}</p>
                          <p className="text-xs text-neutral-500">{bio.enrolled_users.toLocaleString()} enrolled users</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-2 bg-black/30 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-purple-500 to-violet-500 rounded-full"
                              style={{ width: `${bio.accuracy}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-white w-12 text-right">{bio.accuracy}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-white">{biometrics.anomalies_detected_24h}</p>
                    <p className="text-xs text-neutral-400">Anomalies (24h)</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-400">{biometrics.fraud_prevented}</p>
                    <p className="text-xs text-neutral-400">Fraud Prevented</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-orange-400">{biometrics.false_rejection_rate}%</p>
                    <p className="text-xs text-neutral-400">False Rejection Rate</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Use Cases</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {biometrics.use_cases.map((uc) => (
                      <Badge key={uc} variant="outline" className="text-purple-400 border-purple-500/30">
                        <CheckCircle className="w-3 h-3 mr-1" /> {uc}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Threat Hunting */}
        <TabsContent value="hunting" className="space-y-6">
          {hunting && (
            <>
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4 text-center">
                    <Search className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{hunting.hunting_effectiveness.threats_found_proactively}</p>
                    <p className="text-xs text-neutral-400">Proactive Threats Found</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{hunting.hunting_effectiveness.avg_lead_time_hours}h</p>
                    <p className="text-xs text-neutral-400">Avg Lead Time</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#242424] border-white/10">
                  <CardContent className="p-4 text-center">
                    <Shield className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{hunting.hunting_effectiveness.prevention_rate}%</p>
                    <p className="text-xs text-neutral-400">Prevention Rate</p>
                  </CardContent>
                </Card>
              </div>

              {/* Active Hunts */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Active Threat Hunts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {hunting.active_hunts.map((hunt) => (
                      <div key={hunt.name || hunt.id} className="bg-[#1a1a1a] rounded-lg p-4 flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-orange-400 border-orange-500/30 font-mono text-xs">
                              {hunt.hunt_id}
                            </Badge>
                            <Badge className={
                              hunt.risk_level === 'high' ? 'bg-red-500/20 text-red-400' :
                              hunt.risk_level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-green-500/20 text-green-400'
                            }>
                              {hunt.risk_level}
                            </Badge>
                          </div>
                          <p className="text-white font-semibold">{hunt.hypothesis}</p>
                          <p className="text-xs text-neutral-500 mt-1">
                            Indicators found: {hunt.indicators_found}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-neutral-300 border-white/20 capitalize">
                          {hunt.status.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Predictions */}
              <Card className="bg-[#242424] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-400" />
                    AI Threat Predictions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {hunting.predictions.map((pred) => (
                      <div key={pred.threat_type || pred.name} className="bg-[#1a1a1a] rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-semibold">{pred.threat_type}</h4>
                          <Badge className="bg-orange-500/20 text-orange-400">
                            {(pred.probability * 100).toFixed(0)}% probability
                          </Badge>
                        </div>
                        <p className="text-sm text-neutral-400 mb-2">Expected: {pred.expected_timeline}</p>
                        <div className="flex flex-wrap gap-2">
                          {pred.recommended_actions.map((action) => (
                            <Badge key={action} variant="outline" className="text-green-400 border-green-500/30 text-xs">
                              {action}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityIntelligence;
